import React, { useState, useEffect } from 'react';
import { Github, Code, GitPullRequest, Star, Activity, GitFork, Trophy, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in ms

export default function LiveCodingStats({ githubUrl, leetcodeUrl, activeColor, themeType = 'dark' }) {
  const [githubStats, setGithubStats] = useState(null);
  const [leetcodeStats, setLeetcodeStats] = useState(null);
  const [topLanguages, setTopLanguages] = useState([]);
  const [totalStars, setTotalStars] = useState(0);
  const [loading, setLoading] = useState(true);

  const getUsernameFromUrl = (url, type) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      const parts = parsed.pathname.split('/').filter(Boolean);
      if (type === 'github') return parts[0];
      if (type === 'leetcode') {
        return parts[0] === 'u' ? parts[1] : parts[0];
      }
    } catch {
      return null;
    }
  };

  // Cache helper
  const getCached = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const { data, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp > CACHE_DURATION) {
        localStorage.removeItem(key);
        return null;
      }
      return data;
    } catch { return null; }
  };

  const setCache = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
    } catch {}
  };

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      const githubUsername = getUsernameFromUrl(githubUrl, 'github');
      const leetcodeUsername = getUsernameFromUrl(leetcodeUrl, 'leetcode');

      const promises = [];

      if (githubUsername) {
        const cacheKey = `cvlogs_gh_stats_${githubUsername}`;
        const cached = getCached(cacheKey);
        
        if (cached) {
          setGithubStats(cached.stats);
          setTopLanguages(cached.languages || []);
          setTotalStars(cached.totalStars || 0);
        } else {
          promises.push(
            (async () => {
              try {
                // Fetch user profile
                const userRes = await fetch(`https://api.github.com/users/${githubUsername}`);
                const userData = await userRes.json();
                
                if (userData && !userData.message) {
                  const stats = {
                    repos: userData.public_repos,
                    followers: userData.followers,
                    following: userData.following,
                    gists: userData.public_gists,
                    bio: userData.bio,
                    created: userData.created_at
                  };
                  setGithubStats(stats);

                  // Fetch repos for languages and stars
                  try {
                    const reposRes = await fetch(`https://api.github.com/users/${githubUsername}/repos?per_page=100&sort=stars`);
                    const reposData = await reposRes.json();
                    
                    if (Array.isArray(reposData)) {
                      // Calculate total stars
                      const stars = reposData.reduce((sum, r) => sum + (r.stargazers_count || 0), 0);
                      setTotalStars(stars);

                      // Calculate top languages
                      const langCount = {};
                      reposData.forEach(r => {
                        if (r.language) {
                          langCount[r.language] = (langCount[r.language] || 0) + 1;
                        }
                      });
                      const sorted = Object.entries(langCount)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 5)
                        .map(([name, count]) => ({ name, count, pct: Math.round((count / reposData.length) * 100) }));
                      setTopLanguages(sorted);

                      // Cache everything
                      setCache(cacheKey, { stats, languages: sorted, totalStars: stars });
                    }
                  } catch {}
                }
              } catch {}
            })()
          );
        }
      }

      if (leetcodeUsername) {
        const cacheKey = `cvlogs_lc_stats_${leetcodeUsername}`;
        const cached = getCached(cacheKey);

        if (cached) {
          setLeetcodeStats(cached);
        } else {
          promises.push(
            fetch(`https://leetcode-stats-api.herokuapp.com/${leetcodeUsername}`)
              .then(res => res.json())
              .then(data => {
                if (data && data.status === 'success') {
                  const stats = {
                    totalSolved: data.totalSolved,
                    totalQuestions: data.totalQuestions,
                    easySolved: data.easySolved,
                    totalEasy: data.totalEasy,
                    mediumSolved: data.mediumSolved,
                    totalMedium: data.totalMedium,
                    hardSolved: data.hardSolved,
                    totalHard: data.totalHard,
                    ranking: data.ranking,
                    acceptanceRate: data.acceptanceRate,
                    contributionPoints: data.contributionPoints
                  };
                  setLeetcodeStats(stats);
                  setCache(cacheKey, stats);
                }
              })
              .catch(() => {})
          );
        }
      }

      await Promise.all(promises);
      setLoading(false);
    };

    fetchStats();
  }, [githubUrl, leetcodeUrl]);

  if (!githubUrl && !leetcodeUrl) return null;
  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-sm my-4 animate-pulse ${themeType === 'light' ? 'text-slate-500' : 'text-slate-400'}`}>
        <Activity className="w-4 h-4" />
        <span>Syncing live coding stats...</span>
      </div>
    );
  }

  if (!githubStats && !leetcodeStats) return null;

  const colorClass = activeColor?.primary || 'text-blue-500';
  const isLight = themeType === 'light';

  // Language color map
  const langColors = {
    JavaScript: '#f1e05a', TypeScript: '#3178c6', Python: '#3572a5', Java: '#b07219',
    Go: '#00add8', Rust: '#dea584', C: '#555555', 'C++': '#f34b7d', 'C#': '#178600',
    Ruby: '#701516', PHP: '#4f5d95', Swift: '#f05138', Kotlin: '#A97BFF', Dart: '#00b4ab',
    HTML: '#e34c26', CSS: '#563d7c', Shell: '#89e051', Vue: '#41b883', Svelte: '#ff3e00'
  };

  return (
    <div className="space-y-4 my-8 w-full max-w-4xl mx-auto">
      <h3 className={`text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
        <Activity className={`w-4 h-4 ${colorClass}`} />
        <span>Live Developer Stats</span>
      </h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* GitHub Stats */}
        {githubStats && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-5 rounded-2xl border transition-all ${
              isLight 
                ? 'border-slate-200 bg-white shadow-sm hover:shadow-md' 
                : 'border-slate-700/50 bg-slate-800/40 backdrop-blur-sm hover:bg-slate-800/60'
            }`}
          >
            <div className="flex items-center gap-2 mb-4">
              <Github className={`w-5 h-5 ${isLight ? 'text-slate-700' : 'text-slate-300'}`} />
              <span className={`font-bold ${isLight ? 'text-slate-800' : 'text-slate-200'}`}>GitHub</span>
            </div>
            
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="space-y-0.5">
                <div className={`text-xl font-black ${isLight ? 'text-slate-900' : 'text-white'}`}>{githubStats.repos}</div>
                <div className={`text-[10px] flex items-center gap-0.5 font-medium ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                  <GitPullRequest className="w-3 h-3" /> Repos
                </div>
              </div>
              <div className="space-y-0.5">
                <div className={`text-xl font-black ${isLight ? 'text-slate-900' : 'text-white'}`}>{totalStars}</div>
                <div className={`text-[10px] flex items-center gap-0.5 font-medium ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                  <Star className="w-3 h-3" /> Stars
                </div>
              </div>
              <div className="space-y-0.5">
                <div className={`text-xl font-black ${isLight ? 'text-slate-900' : 'text-white'}`}>{githubStats.followers}</div>
                <div className={`text-[10px] flex items-center gap-0.5 font-medium ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                  <TrendingUp className="w-3 h-3" /> Followers
                </div>
              </div>
            </div>

            {/* Top Languages */}
            {topLanguages.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-slate-200/50 dark:border-slate-700/30">
                <div className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>Top Languages</div>
                <div className="flex gap-1 h-2 rounded-full overflow-hidden">
                  {topLanguages.map((lang, i) => (
                    <div key={i} style={{ width: `${lang.pct}%`, backgroundColor: langColors[lang.name] || '#6b7280' }}
                      className="h-full first:rounded-l-full last:rounded-r-full" title={`${lang.name}: ${lang.pct}%`} />
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {topLanguages.map((lang, i) => (
                    <span key={i} className={`text-[10px] flex items-center gap-1 font-medium ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                      <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: langColors[lang.name] || '#6b7280' }} />
                      {lang.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* LeetCode Stats */}
        {leetcodeStats && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`p-5 rounded-2xl border transition-all ${
              isLight 
                ? 'border-amber-200 bg-amber-50/50 shadow-sm hover:shadow-md' 
                : 'border-amber-500/20 bg-amber-900/10 backdrop-blur-sm hover:bg-amber-900/20'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-amber-500" />
                <span className={`font-bold ${isLight ? 'text-amber-700' : 'text-amber-400'}`}>LeetCode</span>
              </div>
              {leetcodeStats.ranking && (
                <div className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                  isLight 
                    ? 'bg-amber-100 text-amber-600 border-amber-200' 
                    : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                }`}>
                  <Trophy className="w-3 h-3 inline mr-0.5" />
                  {leetcodeStats.ranking.toLocaleString()}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-6">
              <div className="space-y-1">
                <div className={`text-3xl font-black ${isLight ? 'text-slate-900' : 'text-white'}`}>{leetcodeStats.totalSolved}</div>
                <div className={`text-[10px] uppercase tracking-widest font-bold ${isLight ? 'text-amber-600' : 'text-amber-500/80'}`}>Solved</div>
              </div>
              <div className="flex-1 space-y-2 text-xs font-bold">
                <div className="flex justify-between items-center">
                  <span className="text-emerald-500">Easy</span>
                  <span className={isLight ? 'text-slate-700' : 'text-slate-300'}>
                    {leetcodeStats.easySolved}{leetcodeStats.totalEasy ? `/${leetcodeStats.totalEasy}` : ''}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-amber-500">Medium</span>
                  <span className={isLight ? 'text-slate-700' : 'text-slate-300'}>
                    {leetcodeStats.mediumSolved}{leetcodeStats.totalMedium ? `/${leetcodeStats.totalMedium}` : ''}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-rose-500">Hard</span>
                  <span className={isLight ? 'text-slate-700' : 'text-slate-300'}>
                    {leetcodeStats.hardSolved}{leetcodeStats.totalHard ? `/${leetcodeStats.totalHard}` : ''}
                  </span>
                </div>
              </div>
            </div>

            {/* Acceptance Rate */}
            {leetcodeStats.acceptanceRate && (
              <div className={`mt-3 pt-3 border-t ${isLight ? 'border-amber-200/50' : 'border-amber-500/10'}`}>
                <div className="flex justify-between items-center text-[10px] font-bold">
                  <span className={isLight ? 'text-amber-600/60' : 'text-amber-500/50'}>Acceptance Rate</span>
                  <span className={isLight ? 'text-amber-700' : 'text-amber-400'}>{Math.round(leetcodeStats.acceptanceRate)}%</span>
                </div>
                <div className={`w-full h-1.5 rounded-full mt-1.5 ${isLight ? 'bg-amber-100' : 'bg-amber-900/30'}`}>
                  <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all" 
                    style={{ width: `${Math.min(leetcodeStats.acceptanceRate, 100)}%` }} />
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
